import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { MoreHorizontal, ArrowRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'
import type { WorkflowVersion } from '@/schemas/workflow'

interface VersionRowProps {
  version: WorkflowVersion
  isLatest: boolean
  isFirst?: boolean
  isLast?: boolean
  onDiff: (version: WorkflowVersion) => void
  onDiffVsLatest: (version: WorkflowVersion) => void
  onRestore: (version: WorkflowVersion) => void
  onExport: (version: WorkflowVersion) => void
}

/**
 * Formats an ISO timestamp as pt-BR `DD MMM YYYY · HH:mm`
 * (e.g. `19 abr 2026 · 14:32`).
 */
function formatTimestamp(iso: string): string {
  try {
    const d = new Date(iso)
    const fmt = new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })
    const parts = fmt.formatToParts(d)
    const pick = (type: string) =>
      parts.find((p) => p.type === type)?.value ?? ''
    const month = pick('month').replace(/\.$/, '')
    return `${pick('day')} ${month} ${pick('year')} · ${pick('hour')}:${pick('minute')}`
  } catch {
    return iso
  }
}

export function VersionRow({
  version,
  isLatest,
  isFirst = false,
  isLast = false,
  onDiff,
  onDiffVsLatest,
  onRestore,
  onExport,
}: VersionRowProps) {
  const { t } = useTranslation('workflows')

  const handleRowClick = useCallback(
    (e: React.MouseEvent) => {
      const target = e.target as HTMLElement
      if (target.closest('[data-version-actions]')) return
      onDiff(version)
    },
    [onDiff, version]
  )

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleRowClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onDiff(version)
        }
      }}
      data-testid={`version-row-${version.version_number}`}
      className={cn(
        'group relative grid items-center gap-4 pl-14 pr-4 py-4 cursor-pointer transition-colors',
        'hover:bg-white/[0.02] focus-visible:bg-white/[0.03] outline-none',
        'focus-visible:ring-1 focus-visible:ring-primary/40 focus-visible:ring-inset'
      )}
      style={{
        gridTemplateColumns: '96px 1fr 180px',
      }}
    >
      {/* Rail column — vertical line + marker */}
      <span
        aria-hidden
        className={cn(
          'absolute left-6 w-px bg-border',
          isFirst ? 'top-1/2' : 'top-0',
          isLast ? 'bottom-1/2' : 'bottom-0'
        )}
      />
      <span
        aria-hidden
        className={cn(
          'absolute left-6 top-1/2 -translate-x-1/2 -translate-y-1/2',
          'flex items-center justify-center'
        )}
      >
        {isLatest ? (
          <>
            <span
              className="absolute inline-block w-4 h-4 rounded-full bg-emerald-500/20 animate-ping"
              style={{ animationDuration: '2.4s' }}
            />
            <span className="relative inline-block w-3 h-3 rounded-full bg-emerald-400 shadow-[0_0_0_3px_hsl(var(--background)),0_0_14px_rgba(52,211,153,0.55)]" />
          </>
        ) : (
          <span className="relative inline-block w-2.5 h-2.5 rounded-full border border-border bg-background" />
        )}
      </span>

      {/* Col 1: v{n} — editorial stamp */}
      <div
        className={cn(
          'font-mono leading-none tabular-nums select-none',
          'text-[30px] tracking-tighter',
          isLatest ? 'text-foreground' : 'text-muted-foreground/80'
        )}
      >
        <span className="text-muted-foreground/50 text-[18px] align-top mr-0.5">
          v
        </span>
        {version.version_number}
      </div>

      {/* Col 2: author + timestamp + note */}
      <div className="flex flex-col justify-center min-w-0 gap-0.5">
        <div className="flex items-center gap-2 text-sm text-foreground">
          <span className="font-medium truncate">
            {version.created_by_id ?? '—'}
          </span>
          <span
            aria-hidden
            className="inline-block h-[3px] w-[3px] rounded-full bg-muted-foreground/50"
          />
          <span className="text-muted-foreground text-xs tabular-nums">
            {formatTimestamp(version.created_at)}
          </span>
        </div>
        <div
          className={cn(
            'text-xs line-clamp-1',
            version.note
              ? 'text-muted-foreground/90'
              : 'italic text-muted-foreground/50'
          )}
        >
          {version.note || t('lifecycle.history.row.noteEmpty')}
        </div>
      </div>

      {/* Col 3: state label + compare hint (hover) + dropdown */}
      <div
        className="flex items-center justify-end gap-3"
        data-version-actions
      >
        <div
          aria-hidden
          className={cn(
            'flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em]',
            'opacity-0 translate-x-1 transition-all duration-200',
            'group-hover:opacity-100 group-hover:translate-x-0',
            'text-muted-foreground/80'
          )}
        >
          <span>Comparar</span>
          <ArrowRight className="w-3 h-3" />
        </div>

        {isLatest && (
          <span
            data-testid="atual-dot"
            aria-label={t('lifecycle.history.row.currentBadge')}
            className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] font-medium text-emerald-300"
          >
            <span
              aria-hidden
              className="inline-block w-1 h-1 rounded-full bg-emerald-400"
            />
            {t('lifecycle.history.row.currentBadge')}
          </span>
        )}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              aria-label={t('lifecycle.history.row.actionsLabel', {
                n: version.version_number,
              })}
              onClick={(e) => e.stopPropagation()}
            >
              <MoreHorizontal className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={() => onDiff(version)}>
              {t('lifecycle.diff.rowAction')}
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => onDiffVsLatest(version)}>
              {t('lifecycle.diff.rowActionVsCurrent')}
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => onRestore(version)}>
              {t('lifecycle.restore.rowAction')}
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => onExport(version)}>
              {t('lifecycle.export.rowAction')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  )
}
